/**
 * Win-probability model and move classification. The constants follow the
 * logistic curve Lichess publishes for its accuracy metric, fitted on rated games.
 */
import { scoreToCp, type Score } from "./uci";

/** White's expected score in percent (0..100) for a White-POV evaluation. */
export function winPercent(score: Score): number {
  if (score.mate !== undefined) {
    if (score.mate === 0) return 50;
    return score.mate > 0 ? 100 : 0;
  }
  const cp = Math.max(-1000, Math.min(1000, scoreToCp(score)));
  return 50 + 50 * (2 / (1 + Math.exp(-0.00368208 * cp)) - 1);
}

/** Per-move accuracy (0..100) from the mover's drop in win percentage. */
export function moveAccuracy(winBefore: number, winAfter: number): number {
  const drop = Math.max(0, winBefore - winAfter);
  const acc = 103.1668 * Math.exp(-0.04354 * drop) - 3.1669;
  return Math.max(0, Math.min(100, acc));
}

export type Classification = "best" | "excellent" | "good" | "inaccuracy" | "mistake" | "blunder" | "book" | "forced";

export const CLASS_META: Record<Classification, { label: string; symbol: string; tone: string }> = {
  book: { label: "Book", symbol: "", tone: "text-sky-500" },
  forced: { label: "Forced", symbol: "", tone: "text-stone-400" },
  best: { label: "Best", symbol: "!", tone: "text-emerald-500" },
  excellent: { label: "Excellent", symbol: "", tone: "text-emerald-400" },
  good: { label: "Good", symbol: "", tone: "text-stone-400" },
  inaccuracy: { label: "Inaccuracy", symbol: "?!", tone: "text-amber-500" },
  mistake: { label: "Mistake", symbol: "?", tone: "text-orange-500" },
  blunder: { label: "Blunder", symbol: "??", tone: "text-red-500" },
};

/**
 * Classifies a move from the mover's win percentage before and after it.
 * Thresholds match Lichess: a drop of 5/10/15 points is an inaccuracy/mistake/blunder.
 */
export function classifyMove(opts: {
  moverWinBefore: number;
  moverWinAfter: number;
  playedUci: string;
  bestUci?: string;
  legalMoveCount?: number;
  isBook?: boolean;
}): Classification {
  if (opts.isBook) return "book";
  if (opts.legalMoveCount === 1) return "forced";
  const drop = opts.moverWinBefore - opts.moverWinAfter;
  if (opts.bestUci && opts.playedUci === opts.bestUci) return "best";
  if (drop >= 15) return "blunder";
  if (drop >= 10) return "mistake";
  if (drop >= 5) return "inaccuracy";
  if (drop <= 1) return "excellent";
  return "good";
}

/** Average accuracy for a list of per-move accuracies (harmonic-leaning mean, like Lichess). */
export function gameAccuracy(accs: number[]): number {
  if (accs.length === 0) return 0;
  const mean = accs.reduce((a, b) => a + b, 0) / accs.length;
  const harmonic = accs.length / accs.reduce((a, b) => a + 1 / Math.max(1, b), 0);
  return (mean + harmonic) / 2;
}
